// (Exam Mode) Past board papers — the last ~20 years of year-tagged practice
// papers for the student's board+grade. Pick a year → starts an exam session.
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { decodeClaims, boardGradeLabel } from '@/lib/auth';
import type { PaperSummary } from '@/types';
import { Card, Button, StudentPage, StudentPageHeader } from '@/components/ds';
import { Icon } from '@/components/garden';

export function PastPapersPage() {
  const claims = useMemo(() => decodeClaims(), []);
  const navigate = useNavigate();

  const papersQuery = useQuery({
    queryKey: ['papers', 'pyq', claims?.board, claims?.grade],
    enabled: Boolean(claims?.board && claims?.grade),
    queryFn: () =>
      apiGet<PaperSummary[]>(`/papers?board=${claims!.board}&grade=${claims!.grade}&pyq=true`),
  });

  const start = useMutation({
    mutationFn: (paperId: string) => apiPost<{ id: string }>('/student-papers', { paperId }),
    onSuccess: (sp) => navigate(`/exam/${sp.id}`),
  });

  const papers = papersQuery.data ?? [];

  return (
    <StudentPage className="gap-4">
      <StudentPageHeader
        title="Past board papers"
        subtitle={`${boardGradeLabel(claims)} · Mathematics — sit a full year's paper under real conditions.`}
        action={
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: 'var(--blue-bg)', color: 'var(--blue-text)' }}
          >
            <Icon name="file-text" size={14} /> <span data-numeric="">{papers.length}</span> papers
          </span>
        }
      />

      {papersQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-card bg-paper-sunk" />
          ))}
        </div>
      ) : papers.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-soft">
            No past papers available yet for your board and grade.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {papers.map((p) => (
            <Card key={p.id} style={{ padding: '16px' }}>
              <div className="flex h-full flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span data-numeric="" className="text-2xl font-extrabold text-forest">
                    {p.examYear ?? '—'}
                  </span>
                  <span
                    className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                    style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
                  >
                    {p.questionCount} Q · {p.totalMarks} marks
                  </span>
                </div>
                <p className="text-xs text-ink-soft">{p.title}</p>
                <div className="mt-auto pt-2">
                  <Button
                    size="sm"
                    disabled={start.isPending}
                    iconLeft={<Icon name="play" size={13} />}
                    onClick={() => start.mutate(p.id)}
                  >
                    {start.isPending && start.variables === p.id ? 'Starting…' : 'Start paper'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {start.isError && (
        <p className="text-sm text-danger">Couldn't start that paper. Please try again.</p>
      )}
    </StudentPage>
  );
}
